/**
 * Avatar utilities for InsightFlow
 * Handles monogram generation and profile picture management
 */

/**
 * Generate user initials for monogram display
 */
export const generateInitials = (name: string, email?: string): string => {
  if (!name && !email) return 'U';
  
  // If we have a name, use it
  if (name && name.trim()) {
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      // Use first letter of first and last name
      return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
    } else {
      // Use first two letters of single name
      return nameParts[0].substring(0, 2).toUpperCase();
    }
  }
  
  // Fallback to email
  if (email) {
    const emailPrefix = email.split('@')[0];
    return emailPrefix.substring(0, 2).toUpperCase();
  }
  
  return 'U';
};

/**
 * Generate a color for the monogram based on the user's name/email
 */
export const generateMonogramColor = (identifier: string): string => {
  const colors = [
    '#f87171', // red-400
    '#fb923c', // orange-400  
    '#fbbf24', // amber-400
    '#facc15', // yellow-400
    '#a3e635', // lime-400
    '#4ade80', // green-400
    '#34d399', // emerald-400
    '#22d3ee', // cyan-400
    '#38bdf8', // sky-400
    '#60a5fa', // blue-400
    '#818cf8', // indigo-400
    '#a78bfa', // violet-400
    '#c084fc', // purple-400
    '#e879f9', // fuchsia-400
    '#f472b6', // pink-400
    '#fb7185', // rose-400
  ];
  
  // Create a simple hash from the identifier
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    const char = identifier.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use absolute value and modulo to get a color index
  const colorIndex = Math.abs(hash) % colors.length;
  return colors[colorIndex];
};

/**
 * Check if a profile picture should be shown based on privacy settings
 */
export const canViewProfilePicture = (
  pictureVisibility: 'public' | 'private' | 'none' | undefined,
  pictureOwnerId: string,
  currentUserId?: string,
  relationship?: 'coach' | 'coachee' | null,
  isAdmin?: boolean
): boolean => {
  if (!pictureVisibility || pictureVisibility === 'none') {
    return false;
  }
  
  if (pictureVisibility === 'public') {
    return true;
  }
  
  if (pictureVisibility === 'private') {
    // Own picture
    if (currentUserId === pictureOwnerId) {
      return true;
    }
    
    // Admin can see private pictures (but not hidden ones)
    if (isAdmin) {
      return true;
    }
    
    // Coach-coachee relationship
    if (relationship) {
      return true;
    }
  }
  
  return false;
};

/**
 * Get the appropriate image URL or return null if not viewable
 */
export const getViewableProfilePictureUrl = (
  profilePictureUrl?: string,
  pictureVisibility?: 'public' | 'private' | 'none',
  pictureOwnerId?: string,
  currentUserId?: string,
  relationship?: 'coach' | 'coachee' | null,
  isAdmin?: boolean
): string | null => {
  if (!profilePictureUrl || !pictureOwnerId) {
    return null;
  }
  
  if (canViewProfilePicture(pictureVisibility, pictureOwnerId, currentUserId, relationship, isAdmin)) {
    return profilePictureUrl;
  }
  
  return null;
}; 